import { useEffect, useRef } from 'react';
import { HttpService } from '@Services/index';
import { BusinessStatus, OnboardingStatus, useBoundStore } from '@Store/index';
import { useQuery } from '@tanstack/react-query';
import { RoutesMap } from '@Router/router.types';
import { useNavigationManager } from '@Router/useNavigationManager';
import { KybData } from '@Store/slices/kyb/kyb.types';
import { GenericMessageTypes } from '@Pages/genericMessagePage/queryParamsSchema';
const MAX_POLLING_TIME = 60000;
export const usePollKybPending = () => {
  const { navigateToSpecificPage } = useNavigationManager();
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    startTimeRef.current = Date.now();
  }, []);

  return useQuery({
    queryFn: getKybData,
    queryKey: ['getKybData'],
    retry: 6,
    retryDelay: 1000,
    refetchInterval: (query) => {
      const { data } = query.state;
      const elapsedTime = startTimeRef.current ? Date.now() - startTimeRef.current : 0;

      if (elapsedTime >= MAX_POLLING_TIME) {
        navigateToSpecificPage(RoutesMap.GENERIC_MESSAGE_PAGE, {
          type: GenericMessageTypes.HANG_TIGHT,
        });
        return false;
      }

      if (data) {
        return handleKybStatus(data, navigateToSpecificPage);
      }
      return false;
    },
  });
};

export const getKybData = async (): Promise<KybData> => {
  const { customerId } = useBoundStore.getState().customer;
  const {
    setKybData,
    onboardingSlice: { getIsEmbedded },
  } = useBoundStore.getState();
  const url = getIsEmbedded()
    ? `v3/secureonboarding/partner/user/${customerId}/status`
    : `v3/secureonboarding/user/${customerId}/status`;

  const { data } = await HttpService.get<KybData>(url);
  setKybData(data);

  return data;
};

const handleKybStatus = (
  data: KybData,
  navigateToSpecificPage: (route: RoutesMap, params?: Record<string, unknown>) => void
) => {
  const onboardingStatus = data?.onboardingStatus;
  const businessStatus = data.business.status;
  const documentsLength = data.business.missingLlcDocuments.length;

  if (
    onboardingStatus === OnboardingStatus.COMPLETED &&
    businessStatus === BusinessStatus.APPROVED
  ) {
    navigateToSpecificPage(RoutesMap.SUCCESS);
  } else if (onboardingStatus === OnboardingStatus.KYB_FAILED_FINAL) {
    navigateToSpecificPage(RoutesMap.GENERIC_MESSAGE_PAGE, {
      type: GenericMessageTypes.KYC_FAILED,
    });
  } else if (onboardingStatus === OnboardingStatus.KYC_FIRST_FAILED) {
    navigateToSpecificPage(RoutesMap.SOCURE_DOC_V);
  } else if (
    onboardingStatus === OnboardingStatus.KYB_PENDING &&
    businessStatus === BusinessStatus.PENDING &&
    !!documentsLength
  ) {
    navigateToSpecificPage(RoutesMap.VERIFICATION_CENTER);
  } else if (
    onboardingStatus === OnboardingStatus.KYB_PENDING &&
    (businessStatus === BusinessStatus.RESUBMIT ||
      businessStatus === BusinessStatus.PENDING_MIDDESK)
  ) {
    navigateToSpecificPage(RoutesMap.GENERIC_MESSAGE_PAGE, {
      type: GenericMessageTypes.HANG_TIGHT,
    });
  } else if (data.business.missingLlcDocuments.length === 0) {
    //   // Continue polling
    return 1000;
  }

  return false;
};
